/**
 * "Add photo" control for ticket activity and the troubleshooting walkthrough.
 *
 * Tap -> the device camera (or photo library) opens via a file input with
 * `capture="environment"`; the chosen photo is shrunk in the browser
 * (utils/imageResize.ts), previewed with an optional caption, then handed to
 * `onUpload`. The caller decides where it goes (which ticket, which step).
 */
import React, { useRef, useState } from "react";

import { Button } from "../ui/button";
import { resizeImageToDataUrl } from "../../utils/imageResize";

interface TicketPhotoUploadProps {
  /** Persist the photo. Throw to keep the preview open and show the error. */
  onUpload: (image: string, caption: string) => Promise<void>;
  /** Button text; defaults to "Add photo". */
  label?: string;
  disabled?: boolean;
}

const MAX_CAPTION = 500;

export const TicketPhotoUpload: React.FC<TicketPhotoUploadProps> = ({
  onUpload,
  label = "Add photo",
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = (): void => {
    setPreview(null);
    setCaption("");
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setBusy(true);
    try {
      setPreview(await resizeImageToDataUrl(file));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onSave = async (): Promise<void> => {
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      await onUpload(preview, caption.trim());
      reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => void onPick(e)}
        aria-label="Choose a photo"
      />
      {!preview && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled || busy}
          onClick={() => inputRef.current?.click()}
          className="min-h-[44px] md:min-h-0"
        >
          {busy ? "Preparing…" : `📷 ${label}`}
        </Button>
      )}

      {preview && (
        <div className="rounded border border-secondary-200 bg-white p-2 space-y-2">
          <img
            src={preview}
            alt="Selected"
            className="max-h-48 rounded border border-secondary-200"
          />
          <input
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value.slice(0, MAX_CAPTION))}
            placeholder="Caption (optional): what does this show?"
            className="w-full text-sm border border-secondary-300 rounded px-2 py-1"
          />
          <div className="flex gap-2 justify-end">
            <Button type="button" size="sm" variant="outline" disabled={busy} onClick={reset}>
              Cancel
            </Button>
            <Button type="button" size="sm" disabled={busy} onClick={() => void onSave()}>
              {busy ? "Uploading…" : "Upload photo"}
            </Button>
          </div>
        </div>
      )}

      {error && (
        <div className="text-xs text-error-700 bg-error-50 px-2 py-1 rounded">{error}</div>
      )}
    </div>
  );
};

export default TicketPhotoUpload;
